/**
 * @fileoverview Data slide-in panel (Human + Raw Collection tabs).
 * @module inspection-renderer/data-panel
 *
 * Responsibilities:
 * - Render the canonical right-side panel for preview + conduct (§9):
 *   - Tab 1 "Human-readable": Inspection Overview, Forms summary,
 *     Per-widget rows with status pills, Rules tracker, Violations log,
 *     Contacts log, Attachments log, Sync state (conduct only).
 *   - Tab 2 "Raw Collection": BSON-extended-JSON view of the
 *     `cases` document, contacts deltas, attachments — G-023 negative:
 *     no separate `loggedViolations` block (violations are nested under
 *     responses).
 * - Preview mode surfaces a Reset button; conduct mode hides it (§9.4).
 * - Pure presentation. Reads from `PersistAdapter` outputs +
 *   `ResolvedFormStateV2` engine output + the renderer-local logged
 *   violations / contact deltas.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `@firesquirrel/core-domain` canonical types + rule trace.
 *
 * Architecture:
 * - Presentation-only. No persistence; the host wires `onReset` to call
 *   the session adapter's `reset()`. No effects.
 *
 * @component DataSlideIn
 *
 * @contract DataSlideInProps
 * Purpose: prop contract for the Data slide-in panel.
 */
import { useMemo, useState, type ReactNode } from 'react';
import type {
  InspectionForm,
  ResolvedFormStateV2,
  RuleTraceEntry,
  CanonicalQuestionResponse,
  AttachmentRefLike,
} from '@firesquirrel/core-domain';
import type { LoggedViolation, ContactChange } from '../violations/types';
import type { SyncState } from '../adapters/persistAdapter';

export interface DataSlideInProps {
  mode: 'preview' | 'conduct';
  caseId: string;
  inspectorId: string;
  entityName?: string;
  templateName?: string;
  currentEventIndex: number;
  openedAt: string;
  forms: InspectionForm[];
  /** Engine-v2 resolved state per form. */
  resolvedStates: Record<string, ResolvedFormStateV2>;
  /** Combined form + template rule trace (engine v2 output). */
  rulesTrace: RuleTraceEntry[];
  /** Canonical responses (PersistAdapter.getCanonicalResponses()). */
  responses: Record<string, CanonicalQuestionResponse>;
  /** Logged violations (PersistAdapter.getViolations()). */
  violations: LoggedViolation[];
  /** Contact-list deltas (PersistAdapter.getContactChanges()). */
  contactChanges: ContactChange[];
  /** Per-form sync state (conduct only). */
  syncState?: Record<string, SyncState>;
  /** Reset handler (preview only). */
  onReset?: () => void;
}

type Tab = 'human' | 'raw';

function flattenAttachments(responses: Record<string, CanonicalQuestionResponse>): AttachmentRefLike[] {
  const out: AttachmentRefLike[] = [];
  for (const r of Object.values(responses)) {
    if (r.attachments) out.push(...r.attachments);
  }
  return out;
}

export function DataSlideIn(props: DataSlideInProps): ReactNode {
  const {
    mode, caseId, inspectorId, entityName, templateName, currentEventIndex, openedAt,
    forms, resolvedStates, rulesTrace, responses, violations, contactChanges, syncState,
    onReset,
  } = props;

  const [tab, setTab] = useState<Tab>('human');
  const [rawNormalized, setRawNormalized] = useState<boolean>(false);

  const attachments = useMemo(() => flattenAttachments(responses), [responses]);

  return (
    <aside data-slide-in className="flex flex-col h-full bg-white border-l border-gray-200">
      <header className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-800">Data</h2>
        <nav role="tablist" className="ml-auto inline-flex gap-1">
          <button
            type="button"
            role="tab"
            data-tab="human"
            aria-selected={tab === 'human'}
            onClick={() => setTab('human')}
            className={`px-2 py-1 text-xs rounded-sm ${tab === 'human' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Human
          </button>
          <button
            type="button"
            role="tab"
            data-tab="raw"
            aria-selected={tab === 'raw'}
            onClick={() => setTab('raw')}
            className={`px-2 py-1 text-xs rounded-sm ${tab === 'raw' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Raw
          </button>
        </nav>
      </header>

      {tab === 'human' ? (
        <HumanReadableTab
          mode={mode}
          caseId={caseId}
          inspectorId={inspectorId}
          entityName={entityName}
          templateName={templateName}
          currentEventIndex={currentEventIndex}
          openedAt={openedAt}
          forms={forms}
          resolvedStates={resolvedStates}
          rulesTrace={rulesTrace}
          responses={responses}
          violations={violations}
          contactChanges={contactChanges}
          attachments={attachments}
          syncState={syncState}
        />
      ) : (
        <RawCollectionTab
          caseId={caseId}
          currentEventIndex={currentEventIndex}
          inspectorId={inspectorId}
          openedAt={openedAt}
          responses={responses}
          attachments={attachments}
          contactChanges={contactChanges}
          violations={violations}
          normalized={rawNormalized}
          onToggleNormalized={() => setRawNormalized((v) => !v)}
        />
      )}

      {mode === 'preview' && onReset && (
        <footer className="mt-auto px-3 py-2 border-t border-gray-200">
          <button
            type="button"
            data-action="reset"
            onClick={onReset}
            className="px-3 py-1.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200"
          >
            Reset session
          </button>
        </footer>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Human-readable
// ---------------------------------------------------------------------------

function HumanReadableTab(props: {
  mode: 'preview' | 'conduct';
  caseId: string;
  inspectorId: string;
  entityName?: string;
  templateName?: string;
  currentEventIndex: number;
  openedAt: string;
  forms: InspectionForm[];
  resolvedStates: Record<string, ResolvedFormStateV2>;
  rulesTrace: RuleTraceEntry[];
  responses: Record<string, CanonicalQuestionResponse>;
  violations: LoggedViolation[];
  contactChanges: ContactChange[];
  attachments: AttachmentRefLike[];
  syncState?: Record<string, SyncState>;
}): ReactNode {
  const {
    mode, caseId, inspectorId, entityName, templateName, currentEventIndex, openedAt,
    forms, resolvedStates, rulesTrace, responses, violations, contactChanges, attachments, syncState,
  } = props;

  return (
    <div role="tabpanel" data-tab-panel="human" className="overflow-y-auto p-3 flex flex-col gap-4 text-xs">
      <section data-section="overview">
        <h3 className="text-xs font-semibold uppercase text-gray-500 mb-1">Inspection Overview</h3>
        <dl className="grid grid-cols-2 gap-1">
          <dt>Case</dt><dd><code>{caseId}</code></dd>
          <dt>Inspector</dt><dd>{inspectorId}</dd>
          {entityName && (<><dt>Entity</dt><dd>{entityName}</dd></>)}
          {templateName && (<><dt>Template</dt><dd>{templateName}</dd></>)}
          <dt>Event</dt><dd>#{currentEventIndex + 1}</dd>
          <dt>Opened</dt><dd>{new Date(openedAt).toLocaleString()}</dd>
        </dl>
      </section>

      {/*
        Forms are rendered as expandable <details> blocks with their widget
        rows nested inside. This consolidates the previously separate
        "Forms" summary + flat "Widgets" list into one tree, so users can
        find a widget under the form they just edited. The outer section
        keeps `data-section="forms-summary"` for backwards compatibility
        with existing tests / E2E selectors that probed the summary text.
      */}
      <section data-section="forms-summary">
        <h3 className="text-xs font-semibold uppercase text-gray-500 mb-1">Forms</h3>
        <ul role="list" className="flex flex-col gap-1">
          {forms.map((form) => {
            const resolved = resolvedStates[form._id];
            const formHidden = !!resolved?.hidden;
            const required = form.fields.filter((f) => f.required).length;
            const answered = form.fields.filter((f) => {
              const r = responses[f.id];
              if (!r) return false;
              if (r.isNA) return true;
              if (r.value == null) return false;
              if (typeof r.value === 'string' && r.value.length === 0) return false;
              if (Array.isArray(r.value) && r.value.length === 0) return false;
              return true;
            }).length;
            const total = form.fields.length;
            const pct = total === 0 ? 0 : Math.round((answered / total) * 100);
            const sync = syncState?.[form._id];
            const formViolations = violations.filter((v) => v.formId === form._id).length;
            return (
              <li
                key={form._id}
                role="listitem"
                data-form-id={form._id}
                data-form-hidden={formHidden ? 'true' : 'false'}
                className="border border-gray-100 rounded-sm"
              >
                <details open data-form-details className="group">
                  <summary className="flex items-center justify-between gap-2 px-2 py-1 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center gap-1">
                      <span aria-hidden className="inline-block w-3 text-[10px] text-gray-400 transition-transform group-open:rotate-90">▶</span>
                      <span className="flex flex-col">
                        <span className="font-medium">{form.display?.sortLabel ?? form.name}</span>
                        <span className="text-[10px] text-gray-500">
                          {answered}/{total} answered • {required} required • {pct}%
                          {formViolations > 0 && (
                            <span className="ml-1 text-red-600">• {formViolations} violation{formViolations === 1 ? '' : 's'}</span>
                          )}
                        </span>
                      </span>
                    </span>
                    {sync && (
                      <span
                        role="status"
                        data-pill="sync"
                        data-sync-state={sync}
                        className={`text-[10px] px-1.5 py-0.5 rounded-full ${syncClass(sync)}`}
                      >
                        {sync}
                      </span>
                    )}
                  </summary>
                  <div data-form-widgets className="px-2 pb-2 pt-0.5 border-t border-gray-100">
                    {form.fields.length === 0 ? (
                      <p className="text-[10px] italic text-gray-400 px-1 py-0.5">No widgets on this form.</p>
                    ) : (
                      <ul role="list" className="flex flex-col gap-1">
                        {form.fields.map((field) => (
                          <WidgetRow
                            key={`${form._id}.${field.id}`}
                            formId={form._id}
                            field={field}
                            response={responses[field.id]}
                            resolved={resolvedStates[form._id]?.fields?.[field.id]}
                            rulesTrace={rulesTrace}
                            violations={violations}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                </details>
              </li>
            );
          })}
        </ul>
      </section>

      <CollapsibleSection
        section="rules-tracker"
        title="Rules Tracker"
        count={rulesTrace.length}
      >
        <ul role="list" className="flex flex-col gap-1">
          {rulesTrace.map((t) => (
            <li
              key={t.ruleId}
              role="listitem"
              data-rule-id={t.ruleId}
              data-rule-matched={t.matched ? 'true' : 'false'}
              className="px-2 py-1 border border-gray-100 rounded-sm"
            >
              <div className="flex items-center gap-1">
                <code className="text-[10px] text-gray-700">{t.ruleId}</code>
                <span className="text-[10px] text-gray-500">scope={t.scope}</span>
                <span className={`ml-auto text-[10px] px-1 rounded-sm ${t.matched ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {t.matched ? 'matched' : 'unmatched'}
                </span>
              </div>
              {t.matched && t.appliedActions.length > 0 && (
                <div className="text-[10px] text-gray-600">
                  actions: {t.appliedActions.map((a) => a.action).join(', ')}
                </div>
              )}
              {!t.matched && t.unmatchedBecause && (
                <div data-unmatched-reason className="text-[10px] text-gray-500">
                  failed: <code>{t.unmatchedBecause.failingLeaf.field}</code> {t.unmatchedBecause.failingLeaf.op} <code>{JSON.stringify(t.unmatchedBecause.failingLeaf.value)}</code> (actual {JSON.stringify(t.unmatchedBecause.actualValue)})
                </div>
              )}
            </li>
          ))}
          {rulesTrace.length === 0 && (
            <li className="text-[10px] italic text-gray-400 px-1">No rules evaluated yet.</li>
          )}
        </ul>
      </CollapsibleSection>

      <CollapsibleSection
        section="violations-log"
        title="Violations"
        count={violations.length}
      >
        <ul role="list" className="flex flex-col gap-1">
          {violations.map((v) => (
            <li key={v._id} role="listitem" data-violation-id={v._id} className="px-2 py-1 border border-red-100 bg-red-50/40 rounded-sm">
              <div className="flex items-center gap-1">
                <code className="text-[10px] text-gray-700">{v.codes[0]?.codeSetId} / {v.codes[0]?.code}</code>
                <span className="text-[10px] text-gray-500">{v.source}</span>
                <span className="ml-auto text-[10px] text-gray-500">{v.occurrences.length} occurrence{v.occurrences.length === 1 ? '' : 's'}</span>
              </div>
              {v.occurrences.length > 0 && (
                <ul role="list" data-violation-occurrences className="mt-0.5 pl-3 text-[10px] text-gray-600">
                  {v.occurrences.map((o) => (
                    <li key={o._id} role="listitem" data-occurrence-id={o._id} className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1">
                        <span>{o.location ?? '(no location)'}</span>
                        {o.status === 'resolved' ? (
                          <span data-repair-badge className="px-1 rounded-sm bg-green-100 text-green-700">Repaired</span>
                        ) : o.status === 'closed' ? (
                          <span className="px-1 rounded-sm bg-gray-200 text-gray-600">Closed</span>
                        ) : (
                          <span className="px-1 rounded-sm bg-red-100 text-red-700">Open</span>
                        )}
                      </div>
                      {o.notes && <span className="text-gray-500">{o.notes}</span>}
                      {o.status === 'resolved' && (
                        <div data-repair-details className="text-[9px] text-green-700">
                          {o.fixedAt && <span>Fixed: {new Date(o.fixedAt).toLocaleDateString()}</span>}
                          {o.fixedBy && <span className="ml-1">by {o.fixedBy}</span>}
                          {o.fixedNotes && <span className="ml-1 italic">"{o.fixedNotes}"</span>}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {violations.length === 0 && (
            <li className="text-[10px] italic text-gray-400 px-1">No violations logged.</li>
          )}
        </ul>
      </CollapsibleSection>

      <CollapsibleSection
        section="contacts-log"
        title="Contacts"
        count={contactChanges.length}
      >
        <ul role="list" className="flex flex-col gap-1">
          {contactChanges.map((c, idx) => (
            <li key={`${c.contactId}-${idx}`} role="listitem" data-contact-change-kind={c.kind} className="px-2 py-1 border border-gray-100 rounded-sm">
              <span className="text-[10px] text-gray-600">{c.kind} — {c.name} ({c.roles.join(', ')})</span>
            </li>
          ))}
          {contactChanges.length === 0 && (
            <li className="text-[10px] italic text-gray-400 px-1">No contact changes.</li>
          )}
        </ul>
      </CollapsibleSection>

      <CollapsibleSection
        section="attachments-log"
        title="Attachments"
        count={attachments.length}
      >
        <ul role="list" className="flex flex-col gap-1">
          {attachments.map((a) => (
            <li key={a._id} role="listitem" data-attachment-id={a._id} data-attachment-mode={a.parentType} className="px-2 py-1 border border-gray-100 rounded-sm text-[10px] text-gray-600">
              {a.fileName ?? a._id} — <code>{a.parentType}</code> / {a.kind}
            </li>
          ))}
          {attachments.length === 0 && (
            <li className="text-[10px] italic text-gray-400 px-1">No attachments captured.</li>
          )}
        </ul>
      </CollapsibleSection>

      {mode === 'conduct' && syncState && (
        <CollapsibleSection
          section="sync-state"
          title="Sync"
          count={Object.keys(syncState).length}
        >
          <ul role="list" className="flex flex-col gap-1">
            {Object.entries(syncState).map(([formId, s]) => (
              <li key={formId} role="listitem" data-form-id={formId} data-sync-state={s} className={`px-2 py-1 rounded-sm ${syncClass(s)}`}>
                <code className="text-[10px]">{formId}</code>: {s}
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CollapsibleSection — shared expand/collapse chrome for the side sections.
// ---------------------------------------------------------------------------

function CollapsibleSection(props: {
  section: string;
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}): ReactNode {
  const { section, title, count, defaultOpen = true, children } = props;
  return (
    <section data-section={section}>
      <details open={defaultOpen} className="group">
        <summary className="flex items-center gap-1 cursor-pointer list-none [&::-webkit-details-marker]:hidden mb-1">
          <span aria-hidden className="inline-block w-3 text-[10px] text-gray-400 transition-transform group-open:rotate-90">▶</span>
          <h3 className="text-xs font-semibold uppercase text-gray-500">{title}</h3>
          {typeof count === 'number' && (
            <span className="ml-1 text-[10px] text-gray-400">({count})</span>
          )}
        </summary>
        {children}
      </details>
    </section>
  );
}

// ---------------------------------------------------------------------------
// WidgetRow — single field row, nested inside its parent form's <details>.
// ---------------------------------------------------------------------------

function WidgetRow(props: {
  formId: string;
  field: InspectionForm['fields'][number];
  response: CanonicalQuestionResponse | undefined;
  resolved: ResolvedFormStateV2['fields'][string] | undefined;
  rulesTrace: RuleTraceEntry[];
  violations: LoggedViolation[];
}): ReactNode {
  const { formId, field, response: r, resolved, rulesTrace, violations } = props;
  void formId;
  const requiredRule = !!resolved?.required && !field.required;
  const fieldViolations = violations.filter((v) => v.fieldId === field.id);
  const hasOccurrenceNotes = fieldViolations.some((v) =>
    v.occurrences.some((o) => typeof o.notes === 'string' && o.notes.trim().length > 0),
  );
  const hasOccurrencePhotos = fieldViolations.some((v) =>
    v.occurrences.some((o) => (o.attachments?.length ?? 0) > 0),
  );
  const requireNotesUnmet =
    !!resolved?.requireNotes &&
    !(r?.value && typeof r.value === 'string' && r.value.trim().length > 0) &&
    !hasOccurrenceNotes;
  const requirePhotosUnmet =
    !!resolved?.requirePhotos &&
    (r?.attachments?.length ?? 0) === 0 &&
    !hasOccurrencePhotos;
  const hasViolation = fieldViolations.length > 0;
  const traces = rulesTrace.filter((t) => t.appliedActions.some((a) => 'fieldId' in a && a.fieldId === field.id));

  // Pass/fail pill — derives from violation mappings on the field. Preserved
  // here so the right-rail summary stays in lockstep with the canvas widget.
  let widgetPassFail: 'pass' | 'fail' | 'neutral' | null = null;
  if (!r?.isNA && Array.isArray(field.violationMappings) && field.violationMappings.length > 0) {
    const fakeResp = { value: r?.value, isNA: r?.isNA };
    const anyMatch = field.violationMappings.some((m) => {
      if (m.bindingMode === 'pending-tenant-bind') return false;
      return matchesTrigger(m.trigger, field.id, fakeResp);
    });
    if (r?.value == null) widgetPassFail = 'neutral';
    else widgetPassFail = anyMatch ? 'fail' : 'pass';
  }

  return (
    <li
      role="listitem"
      data-widget-row
      data-field-id={field.id}
      data-widget-type={field.type}
      className="flex flex-col gap-0.5 px-2 py-1 border border-gray-100 rounded-sm"
    >
      <div className="flex items-center gap-1">
        <span className="font-medium">{field.label}</span>
        <code className="text-[10px] text-gray-400">{field.type}</code>
        {(field.required || requiredRule) && (
          <span data-pill="required" data-rule-driven={requiredRule ? 'true' : 'false'} className={`ml-auto text-[10px] px-1 rounded-sm ${requiredRule ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
            required{requiredRule ? '*' : ''}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1 items-center text-[10px] text-gray-600">
        <span>value: <code>{formatValue(r?.value)}</code></span>
        {r?.isNA && <span data-pill="na" className="px-1 rounded-sm bg-gray-200">N/A</span>}
        {resolved?.hidden && <span data-pill="hidden" className="px-1 rounded-sm bg-amber-100 text-amber-800">hidden</span>}
        {resolved?.disabled && <span data-pill="disabled" className="px-1 rounded-sm bg-gray-200">disabled</span>}
        {hasViolation && <span data-pill="has-violation" className="px-1 rounded-sm bg-red-100 text-red-700">violation</span>}
        {widgetPassFail === 'pass' && <span data-pill="pass" className="px-1 rounded-sm bg-green-100 text-green-700">PASS</span>}
        {widgetPassFail === 'fail' && <span data-pill="fail" className="px-1 rounded-sm bg-red-100 text-red-700">FAIL</span>}
        {requireNotesUnmet && <span data-pill="unmet-notes" className="px-1 rounded-sm bg-yellow-100 text-yellow-800">unmet notes</span>}
        {requirePhotosUnmet && <span data-pill="unmet-photos" className="px-1 rounded-sm bg-yellow-100 text-yellow-800">unmet photos</span>}
        {r?.attachments && r.attachments.length > 0 && <span data-attachments-count={r.attachments.length}>{r.attachments.length} photo{r.attachments.length === 1 ? '' : 's'}</span>}
        {r?.updatedAt && <span data-last-touched>{new Date(r.updatedAt).toLocaleTimeString()}</span>}
        {r?.valueSource?.kind && <span data-value-source={r.valueSource.kind} className="px-1 rounded-sm bg-blue-50 text-blue-700">{r.valueSource.kind}</span>}
      </div>
      {traces.length > 0 && (
        <details data-rules-fired>
          <summary className="cursor-pointer text-[10px] text-blue-600">{traces.length} rule{traces.length === 1 ? '' : 's'} fired</summary>
          <ul className="pl-3 list-disc">
            {traces.map((t) => (<li key={t.ruleId}><code>{t.ruleId}</code> — {t.appliedActions.map((a) => a.action).join(', ')}</li>))}
          </ul>
        </details>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — Raw Collection
// ---------------------------------------------------------------------------

function RawCollectionTab(props: {
  caseId: string;
  currentEventIndex: number;
  inspectorId: string;
  openedAt: string;
  responses: Record<string, CanonicalQuestionResponse>;
  attachments: AttachmentRefLike[];
  contactChanges: ContactChange[];
  violations: LoggedViolation[];
  normalized: boolean;
  onToggleNormalized: () => void;
}): ReactNode {
  const { caseId, currentEventIndex, inspectorId, openedAt, responses, attachments, contactChanges, violations, normalized, onToggleNormalized } = props;

  // Build the canonical case doc projection with violations nested under
  // responses per G-023.
  const responsesWithNestedViolations = useMemo(() => {
    const out: Record<string, CanonicalQuestionResponse & { violation?: unknown }> = {};
    for (const [fid, r] of Object.entries(responses)) {
      const fieldViolation = violations.find((v) => v.fieldId === fid);
      out[fid] = fieldViolation ? { ...r, violation: fieldViolation } : { ...r };
    }
    return out;
  }, [responses, violations]);

  const caseDoc = useMemo(() => ({
    _id: normalized ? caseId : { $oid: caseId },
    inspectorId,
    openedAt: normalized ? openedAt : { $date: openedAt },
    currentEventIndex,
    responses: responsesWithNestedViolations,
  }), [caseId, currentEventIndex, inspectorId, openedAt, responsesWithNestedViolations, normalized]);

  function copy(content: string): void {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(content);
    }
  }

  return (
    <div role="tabpanel" data-tab-panel="raw" className="overflow-y-auto p-3 flex flex-col gap-3 text-[10px]">
      <header className="flex items-center gap-2">
        <button
          type="button"
          data-action="toggle-normalized"
          onClick={onToggleNormalized}
          className="px-2 py-1 text-xs rounded-sm bg-gray-100 hover:bg-gray-200"
        >
          {normalized ? 'BSON-extended JSON' : 'Plain JSON'}
        </button>
      </header>
      <RawBlock label="cases" data={caseDoc} onCopy={copy} />
      <RawBlock label="attachments" data={attachments} onCopy={copy} />
      <RawBlock label="contacts (deltas)" data={contactChanges} onCopy={copy} />
      <RawBlock label="inspectableEntities (read-only context)" data={{ note: 'Hosted by the host; renderer treats as read-only.' }} onCopy={copy} />
    </div>
  );
}

function RawBlock(props: { label: string; data: unknown; onCopy: (s: string) => void }): ReactNode {
  const { label, data, onCopy } = props;
  const content = JSON.stringify(data, null, 2);
  return (
    <details data-raw-block={label} open>
      <summary className="flex items-center gap-2 cursor-pointer">
        <span className="font-semibold uppercase text-gray-500">{label}</span>
        <button type="button" data-action="copy" onClick={(e) => { e.preventDefault(); onCopy(content); }} className="ml-auto px-1.5 py-0.5 rounded-sm bg-gray-100 hover:bg-gray-200 text-[10px]">copy</button>
      </summary>
      <pre className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-sm overflow-x-auto"><code>{content}</code></pre>
    </details>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function matchesTrigger(
  trigger: unknown,
  fieldId: string,
  resp: { value: unknown; isNA?: boolean | null | undefined },
): boolean {
  if (!trigger || typeof trigger !== 'object') return false;
  const t = trigger as Record<string, unknown>;
  if (Array.isArray(t.and)) return t.and.every((c) => matchesTrigger(c, fieldId, resp));
  if (Array.isArray(t.or)) return t.or.some((c) => matchesTrigger(c, fieldId, resp));
  if (t.not) return !matchesTrigger(t.not, fieldId, resp);
  if (t.field === fieldId) {
    const op = t.op as string;
    const expected = t.value;
    const actual = resp.value;
    if (op === 'equals') return actual === expected;
    if (op === 'notEquals') return actual !== expected;
    if (op === 'contains') {
      if (Array.isArray(actual)) return actual.includes(expected);
      if (typeof actual === 'string') return actual.includes(String(expected));
      return false;
    }
    if (op === 'gt') return typeof actual === 'number' && typeof expected === 'number' && actual > expected;
    if (op === 'lt') return typeof actual === 'number' && typeof expected === 'number' && actual < expected;
    if (op === 'gte') return typeof actual === 'number' && typeof expected === 'number' && actual >= expected;
    if (op === 'lte') return typeof actual === 'number' && typeof expected === 'number' && actual <= expected;
    if (op === 'in') return Array.isArray(expected) && (expected as unknown[]).includes(actual);
    if (op === 'notIn') return Array.isArray(expected) && !(expected as unknown[]).includes(actual);
    if (op === 'exists') return actual != null && actual !== '';
  }
  return false;
}

function formatValue(v: unknown): string {
  if (v == null) return '∅';
  if (typeof v === 'string') return v.length > 32 ? `"${v.slice(0, 32)}…"` : `"${v}"`;
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 40);
  return String(v);
}

function syncClass(s: SyncState): string {
  switch (s) {
    case 'pending': return 'bg-amber-100 text-amber-800';
    case 'saving': return 'bg-blue-100 text-blue-800';
    case 'synced': return 'bg-green-100 text-green-800';
    case 'failed': return 'bg-red-100 text-red-700';
    case 'conflict': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}
